import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { registrationService, ProviderRegistrationData } from '../services/registrationService';
import { config } from '../config/environment';
import ErrorModal from './ErrorModal';
import AddressFields, { ApiCountry } from './AddressFields';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import MultiSelectDropdown from './MultiSelectDropdown';
import { getServerConfig } from '../config/serverConfig';
import StripeCardForm, { StripeCardFormHandle } from './StripeCardForm';
import { calculateProratedCents } from '../services/billingUtils';

interface CouponResult {
  valid: boolean;
  coupon_type: string;
  description: string;
  base_price_cents: number;
  discount_amount_cents: number;
  final_price_cents: number;
  free_months: number | null;
}

const BILLING_TIERS = [
  { id: 'starter' as const, name: 'Starter', price: 24.99, priceCents: 2499, description: 'Up to 10 sessions/mo', highlighted: false },
  { id: 'growth' as const, name: 'Growth', price: 39.99, priceCents: 3999, description: 'Up to 30 sessions/mo', highlighted: true },
  { id: 'elite' as const, name: 'Elite', price: 69.99, priceCents: 6999, description: 'Unlimited sessions', highlighted: false },
];

const InfoTip = ({ text }: { text: string }) => (
  <span className="relative group inline-flex items-center ml-1 align-middle">
    <span className="w-3.5 h-3.5 rounded-full border border-gray-500 text-gray-500 text-[9px] flex items-center justify-center cursor-help leading-none select-none flex-shrink-0">i</span>
    <span className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 z-[200] hidden group-hover:block w-56 bg-dark-card border border-dark-border text-xs text-gray-300 rounded-lg p-2 shadow-xl">{text}</span>
  </span>
);

const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,18}[a-z0-9])?$/;

const RegSection = React.memo(({ title, isOpen, onToggle, children }: {
  title: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode;
}) => (
  <div className="bg-dark-card border border-dark-border rounded-xl">
    <button
      type="button"
      className={`w-full px-5 py-3.5 flex items-center justify-between hover:bg-dark-hover/40 transition-colors ${isOpen ? 'rounded-t-xl' : 'rounded-xl'}`}
      onClick={onToggle}
    >
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{title}</p>
      <ChevronRightIcon className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
    </button>
    {isOpen && <div className="px-5 pb-5 space-y-4 border-t border-dark-border pt-4">{children}</div>}
  </div>
));
RegSection.displayName = 'RegSection';

function ProviderRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { email?: string; prefill?: { firstName?: string; lastName?: string; phone?: string; email?: string } } | null;
  const prefill = locationState?.prefill;
  // When prefill is present the user is an existing client adding a coach profile —
  // personal fields come from their existing account and cannot be changed here.
  const isExistingUser = !!prefill;
  const [formData, setFormData] = useState({
    firstName: prefill?.firstName ?? '',
    lastName: prefill?.lastName ?? '',
    email: prefill?.email ?? locationState?.email ?? '',
    phone: prefill?.phone ?? '',
    street_address: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'US',
    currency: 'USD',
    website: '',
    profile_description: '',
    specialization: [] as number[],
    yearsOfExperience: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    allow_weekends: false,
    password: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
    agreeToTerms: false
  });

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    personal: true,
    address: true,
    professional: true,
    credentials: true,
    availability: true,
    branding: true,
    account: true,
  });
  const toggleSection = (key: string) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const [specializationOptions, setSpecializationOptions] = useState<{ id: number; name: string }[]>([]);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Email verified in the registration modal before arriving here
  const arrivedWithVerifiedEmail = !!locationState?.email && !isExistingUser;
  const [emailVerified, setEmailVerified] = useState(arrivedWithVerifiedEmail);
  const [emailVerifSent, setEmailVerifSent] = useState(false);
  const [emailVerifSending, setEmailVerifSending] = useState(false);
  const [emailVerifCode, setEmailVerifCode] = useState('');
  const [emailVerifChecking, setEmailVerifChecking] = useState(false);
  const [emailVerifError, setEmailVerifError] = useState('');
  const [lastVerifiedEmail, setLastVerifiedEmail] = useState('');

  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);

  // Credentials & branding
  const [certifications, setCertifications] = useState<{ id: number; code: string; description: string | null }[]>([]);
  const [institutes, setInstitutes] = useState<{ id: number; name: string; description: string | null }[]>([]);
  const [certificationIds, setCertificationIds] = useState<number[]>([]);
  const [credentialNumber, setCredentialNumber] = useState('');
  const [credentialIssueDate, setCredentialIssueDate] = useState('');
  const [instituteId, setInstituteId] = useState<number | null>(null);
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoFileError, setLogoFileError] = useState('');

  // Billing
  const stripeRef = useRef<StripeCardFormHandle>(null);
  const [billingActive, setBillingActive] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [wantTrial, setWantTrial] = useState(true);
  const [selectedTier, setSelectedTier] = useState<'starter' | 'growth' | 'elite'>('growth');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<CouponResult | null>(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);

  useEffect(() => {
    registrationService.getSpecializations().then(setSpecializationOptions);
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then(setApiCountries)
      .catch(() => {});
    fetch(`${config.apiBaseUrl}/coaches/certifications`)
      .then(r => r.ok ? r.json() : [])
      .then(setCertifications)
      .catch(() => {});
    fetch(`${config.apiBaseUrl}/coaches/institutes`)
      .then(r => r.ok ? r.json() : [])
      .then(setInstitutes)
      .catch(() => {});
    getServerConfig().then(cfg => {
      setBillingActive(cfg.billing_charging_active);
      setStripeKey(cfg.stripe_publishable_key);
    });
  }, []);

  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':   return value.trim() ? '' : 'First name is required';
      case 'lastName':    return value.trim() ? '' : 'Last name is required';
      case 'email':
        if (!value.trim()) return 'Email is required';
        return /\S+@\S+\.\S+/.test(value) ? '' : 'Email is invalid';
      case 'phone':
        if (!value.trim()) return 'Business phone is required';
        return isValidPhoneNumber(value) ? '' : 'Enter a valid phone number (include country code)';
      case 'city':        return value.trim() ? '' : 'City is required';
      case 'state_province': return value.trim() ? '' : 'State/Province is required';
      case 'country':     return value.trim() ? '' : 'Country is required';
      case 'website':
        if (!value.trim()) return '';
        return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(value.trim())
          ? '' : 'Please enter a valid website (e.g. example.com)';
      case 'yearsOfExperience': return value ? '' : 'Years of experience is required';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 12) return 'Password must be at least 12 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(value)) return 'Password must contain at least one digit';
        if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain at least one special character';
        return '';
      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        return value === formData.password ? '' : 'Passwords do not match';
      default: return '';
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const iso = e.target.value;
    const match = apiCountries.find(c => c.iso_code === iso);
    setFormData(prev => ({
      ...prev,
      country: iso,
      state_province: '',
      currency: match ? match.currency_code : prev.currency,
    }));
    if (errors['country']) setErrors(prev => ({ ...prev, country: '' }));
  };

  const sendEmailVerification = async (email: string) => {
    setEmailVerifSending(true);
    setEmailVerifError('');
    setEmailVerifSent(false);
    setEmailVerified(false);
    setEmailVerifCode('');
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/pre-register/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (resp.status === 409) {
        setErrors(prev => ({ ...prev, email: 'This email is already registered. Please sign in.' }));
        return;
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setEmailVerifError(data.detail || 'Failed to send code. Try again.');
        return;
      }
      setEmailVerifSent(true);
      setLastVerifiedEmail(email);
    } catch {
      setEmailVerifError('Network error. Please try again.');
    } finally {
      setEmailVerifSending(false);
    }
  };

  const checkEmailVerification = async () => {
    if (emailVerifCode.length !== 6) return;
    setEmailVerifChecking(true);
    setEmailVerifError('');
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/pre-register/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, code: emailVerifCode }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setEmailVerifError(data.detail || 'Incorrect code. Try again.');
        return;
      }
      setEmailVerified(true);
      setErrors(prev => ({ ...prev, email: '' }));
    } catch {
      setEmailVerifError('Network error. Please try again.');
    } finally {
      setEmailVerifChecking(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponValidating(true);
    setCouponError(null);
    setCouponResult(null);
    try {
      const res = await fetch(`${config.apiBaseUrl}/billing/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim(), tier: selectedTier }),
      });
      const data = await res.json();
      if (res.ok) setCouponResult(data);
      else setCouponError(data.detail || 'Invalid coupon code');
    } catch {
      setCouponError('Could not validate coupon. Please try again.');
    } finally {
      setCouponValidating(false);
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    const fieldError = validateField('email', email);
    setErrors(prev => ({ ...prev, email: fieldError }));
    if (!fieldError && email && email !== lastVerifiedEmail && !isExistingUser) {
      sendEmailVerification(email);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone) newErrors.phone = 'Business phone is required';
    else if (!isValidPhoneNumber(formData.phone)) newErrors.phone = 'Enter a valid phone number';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state_province.trim()) newErrors.state_province = 'State/Province is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (formData.website.trim()) {
      const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;
      if (!urlPattern.test(formData.website.trim())) newErrors.website = 'Please enter a valid website (e.g. example.com)';
    }
    if (formData.specialization.length === 0) newErrors.specialization = 'At least one specialization is required';
    if (!formData.yearsOfExperience) newErrors.yearsOfExperience = 'Years of experience is required';
    if (!isExistingUser) {
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 12) newErrors.password = 'Password must be at least 12 characters';
      else if (!/[A-Z]/.test(formData.password)) newErrors.password = 'Password must contain at least one uppercase letter';
      else if (!/[a-z]/.test(formData.password)) newErrors.password = 'Password must contain at least one lowercase letter';
      else if (!/[0-9]/.test(formData.password)) newErrors.password = 'Password must contain at least one digit';
      else if (!/[^A-Za-z0-9]/.test(formData.password)) newErrors.password = 'Password must contain at least one special character';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the Terms of Use to continue.';
    if (!isExistingUser && !emailVerified) newErrors.email = 'Please verify your email address before continuing.';
    if (billingActive && !wantTrial && !agreeToPolicy) newErrors.agreeToPolicy = 'Please accept the subscription policy to continue.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    setFormSubmitted(true);
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const effectivePassword = isExistingUser ? 'IgnoredByBackend1!' : formData.password;
      const registrationData: ProviderRegistrationData = {
        email: formData.email,
        password: effectivePassword,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        street_address: formData.street_address,
        city: formData.city,
        state_province: formData.state_province,
        postal_code: formData.postal_code,
        country: formData.country,
        currency: formData.currency || undefined,
        website: formData.website,
        profile_description: formData.profile_description,
        specialization: formData.specialization,
        yearsOfExperience: formData.yearsOfExperience,
        timezone: formData.timezone,
        working_hours_start: formData.working_hours_start,
        working_hours_end: formData.working_hours_end,
        allow_weekends: formData.allow_weekends,
        confirmPassword: effectivePassword,
        agreeToTerms: formData.agreeToTerms,
        email_pre_verified: emailVerified,
        slug: slug || undefined,
        certification_ids: certificationIds.length ? certificationIds : undefined,
        credential_number: credentialNumber || undefined,
        credential_issue_date: credentialIssueDate || undefined,
        institute_id: instituteId,
      };
      const response = await registrationService.registerProvider(registrationData);
      if (response.success) {
        // Upload logo immediately using the returned token (non-fatal if it fails)
        if (logoFile && response.accessToken) {
          const fd = new FormData();
          fd.append('file', logoFile);
          await fetch(`${config.apiBaseUrl}/coaches/me/logo`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${response.accessToken}` },
            body: fd,
          }).catch(() => {});
        }
        // Billing setup (only when billing is active)
        if (billingActive && response.accessToken && !wantTrial) {
          // Trial not selected: do Stripe setup-intent → confirm card → set default PM → subscribe
          try {
            const siRes = await fetch(`${config.apiBaseUrl}/billing/setup-intent`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${response.accessToken}` },
            });
            if (siRes.ok) {
              const { client_secret } = await siRes.json();
              if (stripeRef.current && client_secret) {
                const confirmResult = await stripeRef.current.confirmSetup(client_secret);
                if ('paymentMethodId' in confirmResult) {
                  await fetch(`${config.apiBaseUrl}/billing/set-default-payment-method`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${response.accessToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ payment_method_id: confirmResult.paymentMethodId }),
                  }).catch(() => {});
                  await fetch(`${config.apiBaseUrl}/billing/subscribe`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${response.accessToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tier: selectedTier, coupon_code: couponCode.trim() || null }),
                  }).catch(() => {});
                }
              }
            }
          } catch {
            // Non-fatal — user can subscribe from billing tab after login
          }
        }
        // If wantTrial=true, start the 14-day trial immediately after registration
        if (billingActive && response.accessToken && wantTrial) {
          fetch(`${config.apiBaseUrl}/billing/start-trial`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${response.accessToken}` },
          }).catch(() => {});
        }
        setSubmitSuccess(response.message || 'Registration successful!');
        setTimeout(() => {
          navigate('/login/provider', {
            state: { message: 'Registration successful! Please log in to continue.', email: formData.email },
          });
        }, 2000);
      } else {
        const errorMessage = response.message || 'Registration failed. Please try again.';
        setSubmitError(errorMessage);
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      setSubmitError(errorMessage);
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = (field?: string) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted${field && errors[field] ? ' border-red-500' : ''}`;

  return (
    <div className="min-h-screen bg-dark-canvas">


      {/* Registration Form */}
      <div className="flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SparklesIcon className="w-8 h-8 text-primary-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Coach Registration</h1>
            <p className="text-gray-300">Create your account to start managing your practice</p>
          </div>

          {/* Success / Error banners */}
          {submitSuccess && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-lg flex items-center space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-300">{submitSuccess}</p>
            </motion.div>
          )}
          {submitError && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-center space-x-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-300">{submitError}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* ── Personal Information ─────────────────────────────── */}
            <RegSection title="Personal Information" isOpen={openSections['personal']} onToggle={() => toggleSection('personal')}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">First Name *</label>
                  <input type="text" name="firstName" value={formData.firstName}
                    onChange={handleInputChange} onBlur={handleBlur} disabled={isExistingUser}
                    placeholder="Enter your first name"
                    className={`${inputCls('firstName')} ${isExistingUser ? 'opacity-60 cursor-not-allowed' : ''}`} />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Last Name *</label>
                  <input type="text" name="lastName" value={formData.lastName}
                    onChange={handleInputChange} onBlur={handleBlur} disabled={isExistingUser}
                    placeholder="Enter your last name"
                    className={`${inputCls('lastName')} ${isExistingUser ? 'opacity-60 cursor-not-allowed' : ''}`} />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Email Address *</label>
                <div className="relative">
                  <input type="email" name="email" value={formData.email}
                    onChange={e => {
                      handleInputChange(e);
                      if (emailVerified || emailVerifSent) {
                        setEmailVerified(false); setEmailVerifSent(false);
                        setEmailVerifCode(''); setEmailVerifError('');
                      }
                    }}
                    onBlur={handleEmailBlur} disabled={isExistingUser}
                    placeholder="Enter your email address"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.email ? 'border-red-500' : emailVerified ? 'border-green-500' : 'border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted'
                    } ${isExistingUser ? 'opacity-60 cursor-not-allowed' : ''}`} />
                  {emailVerifSending && (
                    <div className="absolute inset-y-0 right-3 flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-primary-400 border-t-transparent rounded-full" />
                    </div>
                  )}
                  {emailVerified && !emailVerifSending && (
                    <div className="absolute inset-y-0 right-3 flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    </div>
                  )}
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                {emailVerified && <p className="text-green-400 text-sm mt-1">Email verified</p>}
                {emailVerifError && !emailVerifSent && <p className="text-red-400 text-sm mt-1">{emailVerifError}</p>}
                {!emailVerified && !emailVerifSent && !emailVerifSending && !isExistingUser &&
                  formData.email && /\S+@\S+\.\S+/.test(formData.email) && !errors.email && (
                  <button type="button" onClick={() => sendEmailVerification(formData.email)}
                    className="mt-2 text-sm text-primary-400 hover:text-primary-300 underline transition-colors">
                    Send verification code
                  </button>
                )}
                {emailVerifSent && !emailVerified && !isExistingUser && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-gray-300">
                      A 6-digit code was sent to <span className="text-white font-medium">{formData.email}</span>. Enter it below.
                    </p>
                    <div className="flex gap-2">
                      <input type="text" inputMode="numeric" maxLength={6} value={emailVerifCode}
                        onChange={e => { setEmailVerifCode(e.target.value.replace(/\D/g, '')); setEmailVerifError(''); }}
                        placeholder="000000"
                        className="w-32 px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-center tracking-widest text-lg" />
                      <button type="button" onClick={checkEmailVerification}
                        disabled={emailVerifCode.length !== 6 || emailVerifChecking}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors">
                        {emailVerifChecking ? 'Checking…' : 'Verify'}
                      </button>
                      <button type="button" onClick={() => sendEmailVerification(formData.email)} disabled={emailVerifSending}
                        className="px-3 py-2 text-sm text-gray-400 hover:text-primary-300 transition-colors">
                        Resend
                      </button>
                    </div>
                    {emailVerifError && <p className="text-red-400 text-sm">{emailVerifError}</p>}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Business Phone *</label>
                <ReactPhoneInput
                  country="us"
                  value={formData.phone.replace(/^\+/, '')}
                  onChange={val => { setFormData(p => ({ ...p, phone: val ? `+${val}` : '' })); if (errors.phone) setErrors(p => ({ ...p, phone: '' })); }}
                  disabled={isExistingUser}
                  containerClass="rpi2-dark"
                  inputStyle={{ width: '100%', background: '#0d0b1e', border: `1px solid ${errors.phone ? '#ef4444' : '#2d2b45'}`, borderRadius: '0.5rem', color: '#e2e0f0', fontSize: '0.875rem', height: '38px' }}
                  buttonStyle={{ background: '#1a1830', border: `1px solid ${errors.phone ? '#ef4444' : '#2d2b45'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
                  dropdownStyle={{ background: '#0d0b1e', color: '#e2e0f0' }}
                  enableSearch
                />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>
            </RegSection>

            {/* ── Address ──────────────────────────────────────────── */}
            <RegSection title="Address" isOpen={openSections['address']} onToggle={() => toggleSection('address')}>
              <AddressFields
                country={formData.country}
                state_province={formData.state_province}
                city={formData.city}
                postal_code={formData.postal_code}
                street_address={formData.street_address}
                errors={{ country: errors.country, state_province: errors.state_province, city: errors.city }}
                onChange={handleInputChange}
                onCountryChange={handleCountryChange}
                onBlur={handleBlur}
                countryOptions={apiCountries.length > 0 ? apiCountries : undefined}
              />
              {/* Currency — defaulted from country, but editable */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-dark-border bg-dark-canvas text-gray-200"
                >
                  {(apiCountries.length > 0
                    ? Array.from(new Map(apiCountries.map(c => [c.currency_code, c])).values())
                    : [
                        { currency_code: 'USD', currency_name: 'US Dollar', currency_symbol: '$' },
                        { currency_code: 'CAD', currency_name: 'Canadian Dollar', currency_symbol: 'C$' },
                        { currency_code: 'GBP', currency_name: 'British Pound', currency_symbol: '£' },
                        { currency_code: 'AUD', currency_name: 'Australian Dollar', currency_symbol: 'A$' },
                      ]
                  ).map(c => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {c.currency_code} — {c.currency_name} ({c.currency_symbol})
                    </option>
                  ))}
                </select>
              </div>
            </RegSection>

            {/* ── Professional ─────────────────────────────────────── */}
            <RegSection title="Professional" isOpen={openSections['professional']} onToggle={() => toggleSection('professional')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  About Your Practice <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea name="profile_description" value={formData.profile_description}
                  onChange={handleInputChange} rows={4}
                  className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="Tell clients about your coaching approach, areas of expertise, and what makes your practice unique..." />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Specializations *</label>
                <MultiSelectDropdown
                  options={specializationOptions.map(o => ({ id: o.id, label: o.name }))}
                  selectedIds={formData.specialization}
                  onChange={ids => {
                    setFormData(prev => ({ ...prev, specialization: ids }));
                    if (errors.specialization) setErrors(prev => ({ ...prev, specialization: '' }));
                  }}
                  placeholder="Select specializations…"
                />
                {errors.specialization && <p className="text-red-500 text-sm mt-1">{errors.specialization}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Website <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input type="text" name="website" value={formData.website}
                  onChange={handleInputChange} onBlur={handleBlur}
                  placeholder="yourwebsite.com"
                  className={inputCls('website')} />
                {errors.website && <p className="text-red-500 text-sm mt-1">{errors.website}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">Years of Experience *</label>
                <select name="yearsOfExperience" value={formData.yearsOfExperience}
                  onChange={handleInputChange} onBlur={handleBlur}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.yearsOfExperience ? 'border-red-500' : 'border-dark-border bg-dark-canvas text-gray-200'
                  }`}>
                  <option value="">Select experience</option>
                  <option value="0-1">0-1 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11-15">11-15 years</option>
                  <option value="15+">15+ years</option>
                </select>
                {errors.yearsOfExperience && <p className="text-red-500 text-sm mt-1">{errors.yearsOfExperience}</p>}
              </div>
            </RegSection>

            {/* ── Credentials ──────────────────────────────────────── */}
            <RegSection title="Credentials" isOpen={openSections['credentials']} onToggle={() => toggleSection('credentials')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Coaching Certifications <span className="text-gray-400 font-normal">(optional)</span>
                  <InfoTip text="Select all credentials you hold. Can be updated later in your profile." />
                </label>
                <MultiSelectDropdown
                  options={certifications.map(c => ({ id: c.id, label: c.code }))}
                  selectedIds={certificationIds}
                  onChange={setCertificationIds}
                  placeholder="Select certifications…"
                />
              </div>

              {certificationIds.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-1">
                      Credential Number <span className="text-gray-400 font-normal">(optional)</span>
                      <InfoTip text="Your primary credential ID issued by the certifying body." />
                    </label>
                    <input type="text" value={credentialNumber} onChange={e => setCredentialNumber(e.target.value)}
                      placeholder="e.g. ICF-123456"
                      className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-200 mb-1">
                      Issue Date <span className="text-gray-400 font-normal">(optional)</span>
                      <InfoTip text="The date your certification was issued or last renewed." />
                    </label>
                    <input type="date" value={credentialIssueDate} onChange={e => setCredentialIssueDate(e.target.value)}
                      className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Accredited Coaching Institute <span className="text-gray-400 font-normal">(optional)</span>
                  <InfoTip text="The accredited institution where you trained or hold membership. Can be updated later in your profile." />
                </label>
                <select value={instituteId ?? ''} onChange={e => setInstituteId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm">
                  <option value="">— None —</option>
                  {institutes.map(i => (
                    <option key={i.id} value={i.id}>{i.name}{i.description ? ` — ${i.description}` : ''}</option>
                  ))}
                </select>
              </div>
            </RegSection>

            {/* ── Availability ─────────────────────────────────────── */}
            <RegSection title="Availability" isOpen={openSections['availability']} onToggle={() => toggleSection('availability')}>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Timezone <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select name="timezone" value={formData.timezone} onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm">
                  {formData.timezone && !['America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
                    'America/Anchorage','Pacific/Honolulu','America/Toronto','America/Vancouver',
                    'Europe/London','Europe/Paris','Europe/Berlin','Europe/Amsterdam',
                    'Asia/Kolkata','Asia/Singapore','Asia/Tokyo',
                    'Australia/Sydney','Australia/Melbourne','Australia/Brisbane',
                    'Australia/Adelaide','Australia/Darwin','Australia/Perth','Australia/Hobart',
                    'Africa/Johannesburg','UTC'].includes(formData.timezone) && (
                    <optgroup label="Detected"><option value={formData.timezone}>{formData.timezone}</option></optgroup>
                  )}
                  <optgroup label="United States">
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Chicago">America/Chicago</option>
                    <option value="America/Denver">America/Denver</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="America/Anchorage">America/Anchorage</option>
                    <option value="Pacific/Honolulu">Pacific/Honolulu</option>
                  </optgroup>
                  <optgroup label="Canada">
                    <option value="America/Toronto">America/Toronto</option>
                    <option value="America/Vancouver">America/Vancouver</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/Berlin">Europe/Berlin</option>
                    <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                  </optgroup>
                  <optgroup label="Asia">
                    <option value="Asia/Kolkata">Asia/Kolkata</option>
                    <option value="Asia/Singapore">Asia/Singapore</option>
                    <option value="Asia/Tokyo">Asia/Tokyo</option>
                  </optgroup>
                  <optgroup label="Australia">
                    <option value="Australia/Sydney">Australia/Sydney</option>
                    <option value="Australia/Melbourne">Australia/Melbourne</option>
                    <option value="Australia/Brisbane">Australia/Brisbane</option>
                    <option value="Australia/Adelaide">Australia/Adelaide</option>
                    <option value="Australia/Darwin">Australia/Darwin</option>
                    <option value="Australia/Perth">Australia/Perth</option>
                    <option value="Australia/Hobart">Australia/Hobart</option>
                  </optgroup>
                  <optgroup label="Africa">
                    <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="UTC">UTC</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">
                  Working Hours <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">From</span>
                    <input type="time" name="working_hours_start" value={formData.working_hours_start}
                      onChange={handleInputChange}
                      className="px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">To</span>
                    <input type="time" name="working_hours_end" value={formData.working_hours_end}
                      onChange={handleInputChange}
                      className="px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm" />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" name="allow_weekends" checked={formData.allow_weekends}
                  onChange={handleInputChange} className="w-4 h-4 accent-primary-600" />
                <span className="text-sm text-gray-200">Allow weekend bookings</span>
              </label>
            </RegSection>

            {/* ── Branding ─────────────────────────────────────────── */}
            <RegSection title="Branding" isOpen={openSections['branding']} onToggle={() => toggleSection('branding')}>
              {/* Logo upload */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Company Logo <span className="text-gray-400 font-normal">(optional)</span>
                  <InfoTip text="Your practice logo. Shown in the sidebar in place of 'CoachSight' for your clients. JPEG, PNG, or WebP · max 5 MB." />
                </label>
                {logoPreviewUrl && (
                  <div className="mb-3 inline-flex items-center justify-center rounded-lg border border-dark-border bg-dark-canvas px-4 py-3">
                    <img src={logoPreviewUrl} alt="Logo preview" className="h-10 max-w-[160px] object-contain" />
                  </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="cursor-pointer px-4 py-2 text-sm rounded-lg border border-primary-600 text-primary-400 hover:bg-primary-900/20 transition-colors">
                    {logoFile ? 'Replace Logo' : 'Upload Logo'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0] ?? null;
                        setLogoFileError('');
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) { setLogoFileError('File too large (max 5 MB)'); return; }
                        setLogoFile(file);
                        const reader = new FileReader();
                        reader.onload = ev => setLogoPreviewUrl(ev.target?.result as string ?? null);
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {logoFile && (
                    <button type="button"
                      onClick={() => { setLogoFile(null); setLogoPreviewUrl(null); setLogoFileError(''); }}
                      className="px-4 py-2 text-sm text-red-400 hover:text-red-300 border border-red-900/40 hover:border-red-800 rounded-lg transition-colors">
                      Remove
                    </button>
                  )}
                </div>
                {logoFileError && <p className="text-red-400 text-xs mt-1">{logoFileError}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  Practice Subdomain <span className="text-gray-400 font-normal">(optional)</span>
                  <InfoTip text="Your unique URL — e.g. 'jane-smith' gives you jane-smith.coachsightecr.com. Lowercase letters, numbers and hyphens only. Max 20 characters. Cannot be changed once set." />
                </label>
                <div className="flex gap-2 items-center rounded-lg border border-dark-border overflow-hidden">
                  <input type="text" value={slug}
                    onChange={e => {
                      const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20);
                      setSlug(v); setSlugError('');
                    }}
                    onBlur={() => {
                      if (slug && !SLUG_RE.test(slug)) setSlugError('Must start and end with a letter or number, no consecutive hyphens.');
                    }}
                    placeholder={`${formData.firstName || 'your'}-${formData.lastName || 'name'}`.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20)}
                    maxLength={20}
                    className="flex-1 bg-dark-canvas text-sm text-gray-200 placeholder-dark-muted px-3 py-2 outline-none" />
                  <span className="px-3 py-2 text-sm text-gray-400 whitespace-nowrap border-l border-dark-border bg-dark-canvas select-none">
                    .coachsightecr.com
                  </span>
                  {!slug && formData.firstName && (
                    <button type="button"
                      onClick={() => {
                        const s = `${formData.firstName}-${formData.lastName}`.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 20);
                        setSlug(s); setSlugError('');
                      }}
                      className="px-3 py-2 text-xs text-primary-400 hover:text-primary-300 whitespace-nowrap border-l border-dark-border bg-dark-canvas">
                      Suggest
                    </button>
                  )}
                </div>
                {slugError && <p className="text-red-500 text-xs mt-1">{slugError}</p>}
                {slug && !slugError && (
                  <p className="text-xs text-gray-500 mt-1">Preview: {slug}.coachsightecr.com</p>
                )}
              </div>
            </RegSection>

            {/* ── Account Security ─────────────────────────────────── */}
            {!isExistingUser && (
              <RegSection title="Account Security" isOpen={openSections['account']} onToggle={() => toggleSection('account')}>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Password *</label>
                  <div className="relative">
                    <input type={formData.showPassword ? 'text' : 'password'} name="password" value={formData.password}
                      onChange={handleInputChange} onBlur={handleBlur}
                      placeholder="Create a password"
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.password ? 'border-red-500' : 'border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted'
                      }`} />
                    <button type="button"
                      onClick={() => setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {formData.showPassword ? <EyeSlashIcon className="h-5 w-5 text-dark-muted" /> : <EyeIcon className="h-5 w-5 text-dark-muted" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  <ul className="mt-2 space-y-1">
                    {[
                      { rule: formData.password.length >= 12,          label: 'At least 12 characters' },
                      { rule: /[A-Z]/.test(formData.password),         label: 'One uppercase letter' },
                      { rule: /[a-z]/.test(formData.password),         label: 'One lowercase letter' },
                      { rule: /[0-9]/.test(formData.password),         label: 'One number' },
                      { rule: /[^A-Za-z0-9]/.test(formData.password),  label: 'One special character' },
                    ].map(({ rule, label }) => (
                      <li key={label} className={`flex items-center gap-1.5 text-xs ${rule ? 'text-green-400' : 'text-dark-muted'}`}>
                        <span>{rule ? '✓' : '○'}</span>{label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-1">Confirm Password *</label>
                  <div className="relative">
                    <input type={formData.showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword}
                      onChange={handleInputChange} onBlur={handleBlur}
                      placeholder="Confirm your password"
                      className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.confirmPassword ? 'border-red-500' : 'border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted'
                      }`} />
                    <button type="button"
                      onClick={() => setFormData(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {formData.showConfirmPassword ? <EyeSlashIcon className="h-5 w-5 text-dark-muted" /> : <EyeIcon className="h-5 w-5 text-dark-muted" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                </div>
              </RegSection>
            )}

            {/* ── Subscription Plan / Free Trial ────────────────────── */}
            {billingActive && (() => {
              const now = new Date();
              const tierData = BILLING_TIERS.find(t => t.id === selectedTier)!;
              const effectiveCents = couponResult?.coupon_type !== 'free_months' && couponResult?.final_price_cents
                ? couponResult.final_price_cents
                : tierData.priceCents;
              const proratedCents = calculateProratedCents(effectiveCents, now);
              const monthName = now.toLocaleString('default', { month: 'long' });
              return (
                <div className="bg-dark-card border border-dark-border rounded-xl px-5 py-5 space-y-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Subscription Plan</p>

                  {/* Trial option */}
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wantTrial}
                      onChange={e => setWantTrial(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-primary-600 flex-shrink-0"
                    />
                    <div>
                      <span className="text-sm font-semibold text-white">Start 14-day free trial (no credit card required)</span>
                      <p className="text-xs text-gray-400 mt-0.5">Try CoachSight free for 14 days. No CC needed. Select a plan when the trial ends.</p>
                    </div>
                  </label>

                  {/* Tier grid + CC form — shown when trial unchecked */}
                  {!wantTrial && (
                    <>
                      <p className="text-xs text-gray-500">Choose your plan. First charge is prorated to the end of {monthName}.</p>
                      <div className="grid grid-cols-3 gap-3">
                        {BILLING_TIERS.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { setSelectedTier(t.id); setCouponResult(null); setCouponError(null); }}
                            className={`relative rounded-xl border-2 px-3 py-3 text-left transition-colors ${
                              selectedTier === t.id
                                ? 'border-primary-600 bg-primary-600/10'
                                : 'border-dark-border hover:border-primary-600/50'
                            }`}
                          >
                            {t.highlighted && (
                              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-primary-600 text-white px-2 py-0.5 rounded-full whitespace-nowrap">Most popular</span>
                            )}
                            <p className={`text-sm font-semibold ${selectedTier === t.id ? 'text-primary-400' : 'text-gray-200'}`}>{t.name}</p>
                            <p className="text-base font-bold text-white mt-0.5">${t.price}<span className="text-xs font-normal text-gray-400">/mo</span></p>
                            <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                          </button>
                        ))}
                      </div>

                      {/* Coupon code */}
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1.5">Coupon code (optional)</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={e => { setCouponCode(e.target.value); setCouponResult(null); setCouponError(null); }}
                            onKeyDown={e => e.key === 'Enter' && handleValidateCoupon()}
                            placeholder="Enter code"
                            className="flex-1 px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            type="button"
                            onClick={handleValidateCoupon}
                            disabled={!couponCode.trim() || couponValidating}
                            className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                          >
                            {couponValidating ? 'Checking…' : 'Apply'}
                          </button>
                        </div>
                        {couponError && <p className="text-red-400 text-xs mt-1">{couponError}</p>}
                        {couponResult && (
                          <p className="text-green-400 text-xs mt-1 font-medium">
                            {couponResult.description}
                            {couponResult.coupon_type !== 'free_months' && couponResult.discount_amount_cents > 0 &&
                              ` — $${(couponResult.final_price_cents / 100).toFixed(2)}/mo after discount`}
                          </p>
                        )}
                      </div>

                      {/* Prorated preview */}
                      <div className="bg-dark-hover/30 border border-dark-border/50 rounded-lg px-4 py-3">
                        <p className="text-xs text-gray-400">
                          {couponResult?.coupon_type === 'free_months'
                            ? `First ${couponResult.free_months} month${(couponResult.free_months ?? 1) !== 1 ? 's' : ''} free, then $${tierData.price}/mo`
                            : `First charge: ~$${(proratedCents / 100).toFixed(2)} (prorated for the rest of ${monthName}), then $${(effectiveCents / 100).toFixed(2)}/mo`}
                        </p>
                      </div>

                      {/* Stripe card */}
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1.5">Credit Card *</label>
                        <StripeCardForm ref={stripeRef} publishableKey={stripeKey} disabled={isLoading} />
                      </div>
                    </>
                  )}

                  {/* Subscription policy — only shown when not on trial (CC is being collected) */}
                  {!wantTrial && (
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agreeToPolicy}
                          onChange={e => setAgreeToPolicy(e.target.checked)}
                          className="mt-0.5 h-4 w-4 accent-primary-600 flex-shrink-0"
                        />
                        <span className="text-sm text-gray-200">
                          I understand I will be billed monthly based on my session count. I can cancel or change plans at any time from Settings.
                        </span>
                      </label>
                      {errors.agreeToPolicy && <p className="text-red-500 text-xs mt-1">{errors.agreeToPolicy}</p>}
                    </div>
                  )}
                </div>
              );
            })()}


            {/* ── Terms & Submit ───────────────────────────────────── */}
            <div className="bg-dark-card border border-dark-border rounded-xl px-5 py-4 space-y-4">
              <div className="flex items-start space-x-3">
                <input type="checkbox" id="agreeToTerms" name="agreeToTerms" checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-dark-border rounded" />
                <div className="flex-1">
                  <label htmlFor="agreeToTerms" className="text-sm text-gray-200">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">Terms of Use</a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>
                  </label>
                  {errors.agreeToTerms && <p className="text-red-500 text-sm mt-1">{errors.agreeToTerms}</p>}
                </div>
              </div>

              {formSubmitted && Object.keys(errors).length > 0 && (
                <p className="text-red-400 text-sm text-center">
                  {Object.keys(errors).length} required {Object.keys(errors).length === 1 ? 'field is' : 'fields are'} missing or invalid — please review above.
                </p>
              )}

              <button type="submit" disabled={isLoading}
                className={`w-full font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center ${
                  isLoading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-primary-600 hover:bg-primary-700 text-white'
                }`}>
                {isLoading ? (
                  <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />Creating Account...</>
                ) : (
                  <><CheckIcon className="w-5 h-5 mr-2" />Create Account</>
                )}
              </button>

              <div className="text-center">
                <span className="text-gray-300">Already have an account? </span>
                <Link to="/login/provider" className="text-primary-600 hover:text-primary-700 font-medium">Sign in here</Link>
              </div>
            </div>

          </form>
        </motion.div>
      </div>

      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}
        title="Registration Error" message={errorModalMessage} />
    </div>
  );
}

export default ProviderRegistration;
